import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useOrderingMessage } from "./useOrderingMessage";
import { useTranslation } from "react-i18next";
import { getAccountIsPersonal, getToken } from "../../../lib/auth";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { logout, setAccess } from "../../../store/authSlice";
import { denyAccess } from "../../../api/access";
import { verifyPasswordForLogout } from "../../../api/auth";
import { decodeEdgeTicketPayload } from "../../../lib/edge/edgeTicket";
import LogoutConfirmModal from "./LogoutConfirmModal";

/**
 * Изходът от модула „Маркиране" (профил иконката): при потвърден достъп —
 * отказ на достъпа; на общ терминал без служител — модал с парола; личен
 * акаунт — директен logout. Общ за горната лента (маси) и футъра (продукти).
 */
export function useOrderingLogout() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const message = useOrderingMessage();
  const access = useAppSelector((s) => s.auth.access);
  const reduxToken = useAppSelector((s) => s.auth.token);
  const username = useAppSelector((s) => s.auth.username);
  const [logoutModalOpen, setLogoutModalOpen] = useState(false);
  const [logoutLoading, setLogoutLoading] = useState(false);

  // Чия парола проверява модалът: на притежателя на токена, с който би тръгнала
  // заявката (`api/client.ts` → `getToken(reduxToken)`). Името му трябва и БЕЗ
  // интернет, за да има с какво да се пита касата — Redux го носи след вход, а
  // при вход СРЕЩУ КАСАТА го носи и самият локален билет.
  const tokenOwner =
    username ??
    decodeEdgeTicketPayload(getToken(reduxToken))?.username ??
    null;

  const handleLogout = async () => {
    if (access) {
      try {
        await denyAccess();
      } catch (e) {
        console.error("Deny access error:", e);
      }
      dispatch(setAccess(false));
      navigate("/", { replace: true });
      return;
    }
    if (getAccountIsPersonal() === false) {
      setLogoutModalOpen(true);
    } else {
      await dispatch(logout());
      navigate("/login", { replace: true });
    }
  };

  const handleLogoutConfirm = async (password: string) => {
    setLogoutLoading(true);
    try {
      // В локален режим проверката минава през касата: облачният
      // `/user/verify-password` го няма там и досега изходът офлайн беше
      // невъзможен (`api/auth.ts::verifyPasswordForLogout`).
      const result = await verifyPasswordForLogout(password, tokenOwner);
      if (result.valid) {
        await dispatch(logout());
        navigate("/login", { replace: true });
      } else {
        message.error(t("logoutConfirm.invalidPassword"));
      }
    } catch {
      message.error(t("logoutConfirm.verificationError"));
    } finally {
      setLogoutLoading(false);
      setLogoutModalOpen(false);
    }
  };

  const logoutModal = (
    <LogoutConfirmModal
      open={logoutModalOpen}
      onCancel={() => setLogoutModalOpen(false)}
      onConfirm={handleLogoutConfirm}
      loading={logoutLoading}
    />
  );

  return { handleLogout, logoutModal };
}
